import React from "react";
import { FileSpreadsheet, FileText, ImageIcon, Paperclip } from "lucide-react";
import { Button } from "@shared/ui/button";

interface FilePreviewProps {
  url: string;
  createdDate?: string;
  className?: string;
}

export const FilePreview: React.FC<FilePreviewProps> = ({
  url,
  createdDate,
  className = "",
}) => {
  const [imgError, setImgError] = React.useState(false);
  const fileName = url.split("/").pop() || "File";
  const lowerUrl = url.toLowerCase();
  const isImage = /\.(jpg|jpeg|png|gif|webp)$/.test(lowerUrl);
  const isPDF = lowerUrl.endsWith(".pdf");
  const isWord = /\.(doc|docx)$/.test(lowerUrl);
  const isExcel = /\.(xls|xlsx|csv)$/.test(lowerUrl);

  let iconConfig = {
    icon: Paperclip,
    color: "text-gray-500",
    bg: "bg-gray-50",
    label: "Document",
  };

  if (isPDF) {
    iconConfig = {
      icon: FileText,
      color: "text-red-500",
      bg: "bg-red-50",
      label: "PDF Document",
    };
  } else if (isWord) {
    iconConfig = {
      icon: FileText,
      color: "text-blue-500",
      bg: "bg-blue-50",
      label: "Word Document",
    };
  } else if (isExcel) {
    iconConfig = {
      icon: FileSpreadsheet,
      color: "text-green-500",
      bg: "bg-green-50",
      label: "Excel Spreadsheet",
    };
  }

  return (
    <div className={`space-y-2 ${className}`}>
      <div className="group relative border rounded-lg overflow-hidden bg-gray-50 aspect-video flex items-center justify-center">
        {isImage && !imgError ? (
          <img
            src={url}
            alt="File Preview"
            className="object-cover w-full h-full"
            onError={() => setImgError(true)}
          />
        ) : (
          <div
            className={`flex flex-col items-center justify-center w-full h-full ${iconConfig.color} ${iconConfig.bg}`}
          >
            <iconConfig.icon className="h-10 w-10 mb-1" />
            <span className="text-[10px] font-bold uppercase">
              {iconConfig.label}
            </span>
          </div>
        )}
        <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
          <Button
            variant="secondary"
            size="sm"
            onClick={() => window.open(url, "_blank")}
          >
            View
          </Button>
        </div>
      </div>
      <div className="px-1">
        <p
          className="text-xs font-medium truncate text-gray-700"
          title={fileName}
        >
          {fileName}
        </p>
        {createdDate && (
          <p className="text-[10px] text-gray-500">{createdDate}</p>
        )}
      </div>
    </div>
  );
};
